import React, { useEffect, useState } from 'react';
import { MessageSquareText, Plus, Pencil, Trash2, Check, X, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { api } from '../context/AuthContext';

const Editor = ({ initial, onSave, onCancel }) => {
  const [titel, setTitel] = useState(initial?.titel || '');
  const [text, setText] = useState(initial?.text || '');
  return (
    <div className="p-2 space-y-1.5 bg-[#fbf7f0] border-b border-gray-100" data-testid="quick-reply-editor">
      <input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Titel, z. B. Terminbestätigung" maxLength={80}
        data-testid="quick-reply-title-input" className="w-full px-2 py-1 text-xs border border-gray-200 focus:outline-none focus:border-[#b8893d]" />
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Text des Bausteins"
        data-testid="quick-reply-text-input" className="w-full px-2 py-1 text-xs border border-gray-200 resize-none focus:outline-none focus:border-[#b8893d]" />
      <p className="text-[10px] text-[#7a8a99]">Platzhalter: {'{Name} {Mandantennummer} {Aktenzeichen} {Mitarbeiter}'}</p>
      <div className="flex justify-end gap-1">
        <button type="button" onClick={onCancel} data-testid="quick-reply-cancel" className="p-1 text-[#7a8a99] hover:text-[#1B3A5C]"><X className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => onSave({ titel, text })} disabled={!titel.trim() || !text.trim()} data-testid="quick-reply-save"
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-wider bg-[#1B3A5C] text-white disabled:opacity-40"><Check className="w-3 h-3" /> SPEICHERN</button>
      </div>
    </div>
  );
};

export const fuellen = (text, k = {}) => text.replace(/\{(Name|Mandantennummer|Aktenzeichen|Mitarbeiter)\}/g, (all, key) => k[key] || (key === 'Aktenzeichen' ? '—' : all));

export const QuickReplies = ({ onInsert, isAdmin, platzhalter }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const [err, setErr] = useState('');

  const load = () => api.get('/textbausteine').then((r) => setItems(r.data.items || [])).catch((e) => setErr(e?.response?.data?.detail || 'Laden fehlgeschlagen'));
  useEffect(() => { if (open) load(); }, [open]);

  const save = async (body) => {
    setErr('');
    try {
      if (edit === 'new') await api.post('/textbausteine', body); else await api.patch(`/textbausteine/${edit.id}`, body);
      setEdit(null); load(); window.dispatchEvent(new Event('textbausteine-geaendert'));
    } catch (e) { setErr(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Textbaustein „${b.titel}“ löschen?`)) return;
    try { await api.delete(`/textbausteine/${b.id}`); load(); window.dispatchEvent(new Event('textbausteine-geaendert')); } catch (e) { setErr(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };
  const pick = (b) => { onInsert(fuellen(b.text, platzhalter)); setOpen(false); };
  const shown = items.filter((b) => `${b.titel} ${b.text}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEdit(null); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="quick-replies-btn" title="Vorlagen / Textbausteine"
          className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full transition-colors ${open ? 'bg-[#1B3A5C] text-white' : 'text-[#34536b] hover:bg-[#f5f8fa]'}`}>
          <MessageSquareText className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-80 p-0 rounded-sm" data-testid="quick-replies-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
          <span className="text-[10px] tracking-[0.2em] text-[#1B3A5C] font-semibold">VORLAGEN</span>
          {isAdmin && <button type="button" onClick={() => setEdit('new')} data-testid="quick-reply-new" className="inline-flex items-center gap-1 text-[10px] tracking-wider text-[#b8893d] hover:text-[#1B3A5C]"><Plus className="w-3 h-3" /> NEU</button>}
        </div>
        {edit && <Editor initial={edit === 'new' ? null : edit} onSave={save} onCancel={() => setEdit(null)} />}
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-gray-100">
          <Search className="w-3 h-3 text-[#7a8a99]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" data-testid="quick-reply-search" className="flex-1 text-xs focus:outline-none" />
        </div>
        {err && <p className="px-3 py-1.5 text-[11px] text-red-700 bg-red-50" data-testid="quick-reply-error">{err}</p>}
        <ul className="max-h-64 overflow-y-auto">
          {shown.length === 0 && <li className="px-3 py-4 text-xs text-center text-[#7a8a99]">Noch keine Textbausteine.</li>}
          {shown.map((b) => (
            <li key={b.id} className="group flex items-start gap-1 px-3 py-2 border-b border-gray-50 hover:bg-[#f5f8fa]">
              <button type="button" onClick={() => pick(b)} data-testid={`quick-reply-item-${b.id}`} className="flex-1 text-left min-w-0">
                <span className="block text-xs font-semibold text-[#1B3A5C] truncate">{b.titel}</span>
                <span className="block text-[11px] text-[#34536b]/80 line-clamp-2">{b.text}</span>
              </button>
              {isAdmin && <button type="button" onClick={() => setEdit(b)} data-testid={`quick-reply-edit-${b.id}`} title="Bearbeiten" className="p-1 text-[#7a8a99] hover:text-[#1B3A5C]"><Pencil className="w-3 h-3" /></button>}
              {isAdmin && <button type="button" onClick={() => remove(b)} data-testid={`quick-reply-delete-${b.id}`} title="Löschen" className="p-1 text-[#7a8a99] hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default QuickReplies;
